import { Injectable } from '@angular/core';
import {
  AuthService,
  LoggerService,
  phoneNumberRegExp,
  shareReplayRefCount,
} from '@adreno-common';
import { CoreService } from '@app/core/services';
import {
  RecordingService,
  SchedCheckService,
  WorkService,
} from '@app/work/services';
import { SIPService } from './sip.service';
import { environment } from '@environments/environment';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  from,
  fromEvent,
  merge,
  Observable,
  of,
  Subject,
  timer,
} from 'rxjs';

import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  CallManualRequest,
  LoginRequest,
  NailInRequest,
  HangUpRequest,
  ToggleHoldRequest,
  Agent3WayRequest,
  AgentRecordingRequest,
  CancelGetNextNumberRequest,
  GetNextNumberRequest,
  InboundConnectRequest,
  UnattendedTransferRequest,
  GetTransferAgentsVm,
  GetTransferAgentsChildDto,
  TransferToAgentRequest,
  DiallerSignalRRequests,
  AgentEvent,
  PingEvent,
  InboundWaitingEvent,
  InboundConnectEvent,
  ConnectEvent,
  PowConnectEvent,
  DiallingEvent,
  DisconnectedEvent,
  WaitingEvent,
  ErrorEvent,
  LoggedInEvent,
  IBPopScriptDetails,
  IBTransferOnlyRequest,
  IBTransferOnlyEvent,
  LoginIBTransferOnlyRequest,
  ResetIBQueuesEvent,
  ResetIBQueuesRequest,
  NailOutAndLogoutRequest,
} from '../models';
import { TeleworkerState } from '@app/core/models';
import { HttpClient } from '@angular/common/http';
import { formatDate } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { CallbackService } from '@app/work/services/callback.service';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { Teleworker } from '@app/work/code';
import { CampaignDialType } from '@app/work/models';

@Injectable({
  providedIn: 'root',
})
export class DiallerService {
  private callManualPending?: {
    callerId: string;
    phoneNumber: string;
    timeout: number;
  };
  private callManualPendingTimeout?: any;
  private calltaken = false;
  private campaignid: number = 0;
  private connected$: Observable<HubConnectionState>;
  private dialType?: CampaignDialType;
  private disconnected$ = new Subject<void>();
  private hasIBScheduled: boolean = false;
  private hostname: string = '';
  private hubConn: HubConnection;
  private hubStateSub = new BehaviorSubject<HubConnectionState>(
    HubConnectionState.Disconnected
  );
  private ib: boolean = false;
  private manualOB: boolean = false;
  private nailInTimeout?: any;
  private nailledin: boolean = false;
  private nailuptime = new BehaviorSubject<number>(-1);
  private readonly onCallStates = [
    TeleworkerState.InboundConnected,
    TeleworkerState.OutboundConnected,
    TeleworkerState.HoldIB,
    TeleworkerState.HoldOB,
    TeleworkerState.TransferIB,
    TeleworkerState.TransferOB,
    TeleworkerState.CallDialling,
    TeleworkerState.Ringing,
  ];
  private onVoicemailCall = false;
  private pendingNaiupModal: boolean = false;
  private pendingPopScriptDetails: IBPopScriptDetails | undefined;
  private pendingStopRecording: AgentRecordingRequest | undefined;

  private port: number = 0;
  private retryEmptyQueueTimer?: number;
  private ringing: boolean = false;
  private ringingAudio$ = from(navigator.mediaDevices.enumerateDevices()).pipe(
    map((devices) =>
      devices.find(
        (device) =>
          device.kind === 'audiooutput' && device.deviceId === 'default'
      )
    ),
    filter((audioDevice) => !!audioDevice),
    map((audioDevice) => audioDevice!),
    distinctUntilChanged((a, b) => a.deviceId == b.deviceId),
    switchMap((audioDevice) => {
      var audioElement = new Audio('/assets/sounds/defaultring.wav');
      audioElement.loop = true;
      return from((audioElement as any).setSinkId(audioDevice.deviceId)).pipe(
        map(() => audioElement)
      );
    }),
    switchMap((audioElement) =>
      fromEvent(audioElement, 'canplay').pipe(map(() => audioElement))
    ),
    shareReplay(1)
  );
  private serviceWorker$ = defer(() => {
    if (!isNotificationSupported()) return of('denied');
    if (Notification.permission === 'default')
      return from(Notification.requestPermission());
    return of(Notification.permission);
  }).pipe(
    filter((permission) => permission === 'granted'),
    switchMap((permission) =>
      from(navigator.serviceWorker.getRegistration('/sw.js'))
    ),
    filter((reg) => !!reg),
    map((reg) => reg!),
    shareReplayRefCount()
  );

  private signalrRetries = 0;
  private sipRegistered: boolean = false;
  private socketOpen: boolean = false;
  private usersApi = `${environment.apiPath}/users`;
  private voip: boolean = false;

  agent3WayPending: boolean = false;
  connectedState$: Observable<HubConnectionState>;

  ibxferonly: boolean = false;
  nailInCallLostState: TeleworkerState | undefined = undefined;
  nailuptime$ = this.nailuptime.asObservable();
  onVoiceMailCall$ = this.coreSvc.state$.pipe(
    filter((twState) => {
      switch (twState) {
        case TeleworkerState.Voicemail:
        case TeleworkerState.Work:
        case TeleworkerState.Unavailable:
        case TeleworkerState.UnavailableIB:
        case TeleworkerState.UnavailableOB:
        case TeleworkerState.AvailableIB:
        case TeleworkerState.AvailableOB:
          return true;
        default:
          return false;
      }
    }),
    map((twState) => {
      switch (twState) {
        case TeleworkerState.Voicemail:
          return true;
        default:
          return false;
      }
    }),
    distinctUntilChanged(),
    shareReplayRefCount()
  );

  state$ = this.coreSvc.state$;
  submitting = new BehaviorSubject<boolean>(false);
  transferAgents$: Observable<GetTransferAgentsChildDto[]> | undefined;
  xferStopRecording: boolean = false;

  constructor(
    private logger: LoggerService,
    private coreSvc: CoreService,
    private workSvc: WorkService,
    private sipSvc: SIPService,
    private http: HttpClient,
    private toastr: ToastrService,
    private authSvc: AuthService,
    private recordingSvc: RecordingService,
    private callbackSvc: CallbackService,
    private schedChkSvc: SchedCheckService
  ) {
    this.ibxferonly = false;
    this.connected$ = this.hubStateSub.pipe(
      filter((state) => state === HubConnectionState.Connected)
    );

    this.connectedState$ = this.hubStateSub.asObservable();
    this.initVariables();

    this.recordingSvc.pauseMonitor$
      .pipe(withLatestFrom(this.workSvc.testMode$))
      .subscribe(([pauseMonitor, testMode]) => {
        if (pauseMonitor == true) {
          if (testMode) {
            this.toastr.info('Recording Stopped');
          } else {
            this.stopRecording();
          }
        } else if (pauseMonitor == false) {
          if (testMode) {
            this.toastr.info('Recording Started');
          } else {
            this.startRecording();
          }
        }
      });

    this.sipSvc.deviceLost$
      .pipe(withLatestFrom(this.coreSvc.state$))
      .subscribe(([reason, state]) => {
        this.logger.error('DiallerService', 'deviceLost$', { reason, state })();
        switch (reason) {
          case 'access':
            this.toastr.error(
              'Permission denied. Please allow the site to use your micrphone',
              'Microphone Device',
              {
                disableTimeOut: true,
                closeButton: true,
              }
            );
            break;
          case 'none':
            this.toastr.error(
              'No devices found. Please check you device and try again',
              'Microphone Device',
              {
                disableTimeOut: true,
                closeButton: true,
              }
            );
            break;
          default:
            this.toastr.error(
              'Nail In Call Disconnected',
              'Microphone Device Lost',
              {
                disableTimeOut: true,
                closeButton: true,
              }
            );
        }

        switch (state) {
          case TeleworkerState.AvailableIB:
            this.cancelGetNextNumber();
            this.coreSvc.state = TeleworkerState.UnavailableIB;
            this.workSvc.showStateModal();
            break;
          case TeleworkerState.AvailableOB:
            this.cancelGetNextNumber();
            this.coreSvc.state = TeleworkerState.UnavailableOB;
            this.workSvc.showStateModal();
            break;
          case TeleworkerState.InboundConnected:
            this.hangUp();
            this.coreSvc.state = TeleworkerState.WrapIB;
            break;
          case TeleworkerState.OutboundConnected:
            this.hangUp();
            this.coreSvc.state = TeleworkerState.WrapOB;
            break;
          default:
            //some other, not on an active call, state (Preview, Break, Wrap, etc)
            //not sure if there is anything to do here
            break;
        }
        if (this.nailledin) {
          //assuming we're still online this should tidy things up state-wise
          this.nailOutAndLogout();
        }
        if (!!this.callManualPending) {
          this.callManualPending = undefined;
          this.toastr.warning('Device Lost', 'OB Call Cancelled');
        }
      });

    this.hubConn = this.signalrInit();

    combineLatest([this.sipSvc.nailedIn$, this.connectedState$])
      .pipe(withLatestFrom(this.coreSvc.state$))
      .subscribe(([[nailedin, connected], state]) => {
        if (
          !nailedin &&
          connected === HubConnectionState.Connected &&
          (state === TeleworkerState.AvailableIB ||
            state === TeleworkerState.AvailableOB)
        ) {
          this.cancelGetNextNumber();
          this.coreSvc.state = TeleworkerState.Unavailable;
          this.workSvc.showStateModal();
          this.nailOutAndLogout();
        }
      });

    this.initCoreStateListener();
    this.initTransferCallListener();
    this.initOpenTasksIfAvailableListener();
  }

  private checkNailupTimeout() {
    combineLatest([
      this.nailuptime$,
      this.workSvc.nailupTimeLimit$,
      this.coreSvc.state$,
    ])
      .pipe(take(1))
      .subscribe(([nailupstart, nailuplimit, currentState]) => {
        if (
          !this.pendingNaiupModal &&
          !this.onCallStates.includes(currentState!)
        ) {
          let elapsedtimeminutes = (Date.now() - nailupstart) / 60000;
          if (
            elapsedtimeminutes >= nailuplimit! &&
            !this.voip &&
            this.nailledin
          ) {
            // we need to check if we are in Wrap...if we are we need to wait until the hangup happens
            // before the modal pops up
            if (
              currentState === TeleworkerState.WrapIB ||
              currentState === TeleworkerState.WrapOB ||
              this.workSvc.scriptClosed === false
            ) {
              this.pendingNaiupModal = true;
              // we need to hangup the nailup call now.
              this.nailOutAndLogout();
            } else if (currentState === TeleworkerState.Unavailable) {
              // they are in an AUX state so just do the nailout - don't pop the modal
              this.nailOutAndLogout();
            } else {
              // we need to hangup the nailup call.
              this.nailOutAndLogout();
              this.coreSvc.state = TeleworkerState.Work;
              this.workSvc.openNailupTimeoutModal();
            }
          } else {
            setTimeout(() => this.checkNailupTimeout(), 30000);
          }
        } else {
          setTimeout(() => this.checkNailupTimeout(), 120000);
        }
      });
  }

  // renamed
  private finaliseRecord() {
    this.pendingStopRecording = undefined;

    this.workSvc.scriptClosed = true;
    //agent has closed out of record so no need to prevent normal post nailin actions

    if (!!this.callManualPending) {
      let { phoneNumber, callerId, timeout } = this.callManualPending;
      this.callManualPending = undefined;
      this.callManual(phoneNumber, callerId, timeout);
    } else {
      if (this.dialType === 'Predictive' || this.dialType === 'Preview') {
        // OB predictive command we need to just go available (run getnextnumber) straight away

        this.callbackSvc.callbacksDue$
          .pipe(
            take(1),
            withLatestFrom(
              this.workSvc.tasksPresented$,
              this.nailuptime$,
              this.workSvc.nailupTimeLimit$,
              this.workSvc.campaignId$
            ),
            switchMap(
              ([
                callbacksDue,
                tasksPresented,
                nailupstart,
                nailuplimit,
                campaignId,
              ]) =>
                this.workSvc.isUserScheduled(campaignId!).pipe(
                  withLatestFrom(this.coreSvc.state$),
                  map(([isSched, currentState]) => ({
                    callbacksDue,
                    tasksPresented,
                    nailupstart,
                    nailuplimit,
                    campaignId,
                    isSched,
                    currentState,
                  }))
                )
            )
          )
          .subscribe(
            ({
              callbacksDue,
              tasksPresented,
              nailupstart,
              nailuplimit,
              campaignId,
              isSched,
              currentState,
            }) => {
              if (!isSched) {
                this.logger.info(
                  'DiallerService',
                  'finaliseRecord() not isSched'
                )();
                this.toastr.info('Shift Ended');
                this.workSvc.exit();
                this.workSvc.navigateToWork();
                this.workSvc.unsetWork();
                this.coreSvc.state = TeleworkerState.UnavailableIB;
                this.workSvc.showStateModal();
              } else {
                let elapsedtimeminutes = (Date.now() - nailupstart) / 60000;
                if (elapsedtimeminutes >= nailuplimit! && !this.voip) {
                  // we need to hangup the nailup call.
                  if (this.nailledin) this.nailOutAndLogout();

                  this.coreSvc.state = TeleworkerState.Work;
                  this.workSvc.openNailupTimeoutModal();
                } else {
                  if (callbacksDue && !tasksPresented) {
                    this.workSvc.navigateToWork();
                    this.workSvc.openTasksModal();
                  } else {
                    //
                    if (this.dialType == 'Predictive') {
                      if (this.nailledin) {
                        this.coreSvc.state = TeleworkerState.AvailableOB;
                        // update setTasksPresented to say that we want to see it next time if appropriate
                        this.workSvc.setTasksPresented(false);
                        // pop the messaging script and set the waiting state variable
                        this.workSvc.setWaitingForOB(true);
                        this.getNextNumber();
                      } else {
                        this.nailOutAndLogout();

                        this.coreSvc.state = TeleworkerState.UnavailableOB;
                        this.workSvc.openNailupDroppedModal();
                      }
                    } else {
                      this.workSvc.setTasksPresented(false);
                      if (currentState !== TeleworkerState.UAT)
                        this.workSvc.getPreviewRecord(campaignId!);
                      else this.workSvc.getNextUATRecord();
                    }
                  }
                }
              }
            }
          );
      } else {
        // if we are in Wrap IB then we need to do getNextNumber and move to Available IB
        this.coreSvc.state$
          .pipe(
            take(1),
            withLatestFrom(
              this.workSvc.standbyLimit$,
              this.sipSvc.voip$,
              this.nailuptime$,
              this.workSvc.nailupTimeLimit$,
              this.callbackSvc.callbacksDue$.pipe(
                withLatestFrom(this.workSvc.tasksPresented$)
              )
            )
          )
          .subscribe(
            ([
              currentState,
              standbyLimit,
              voip,
              nailupstart,
              nailuplimit,
              [callbacksDue, tasksPresented],
            ]) => {
              // reset the queue, recordid and campaignid to 0
              this.coreSvc.recordId = 0;
              this.coreSvc.queueId = 0;
              this.coreSvc.campaignId = 0;
              this.manualOB = false;
              this.workSvc.setTasksPresented(false);
              let elapsedtimeminutes = (Date.now() - nailupstart) / 60000;
              if (
                !voip &&
                (currentState === TeleworkerState.WrapIB ||
                  currentState === TeleworkerState.WrapOB) &&
                elapsedtimeminutes >= (nailuplimit || 55)
              ) {
                // we need to hangup the nailup call.
                if (this.nailledin) this.nailOutAndLogout();
                this.nailInCallLostState = undefined;
                this.coreSvc.state = TeleworkerState.Work;
                this.workSvc.openNailupTimeoutModal();
              } else if (callbacksDue && !tasksPresented && !this.calltaken) {
                this.workSvc.navigateToWork();
                this.workSvc.openTasksModal();
              } else if (
                (!voip || this.nailledin) &&
                (currentState === TeleworkerState.WrapIB ||
                  currentState === TeleworkerState.WrapOB ||
                  currentState === TeleworkerState.SIPNailIn ||
                  currentState == TeleworkerState.UnavailableIB)
              ) {
                this.nailInCallLostState = undefined;
                this.coreSvc.state = TeleworkerState.Standby;
                setTimeout(() => this.moveToIBAvailable(), standbyLimit * 1000);
              } else if (currentState === TeleworkerState.Voicemail) {
                this.coreSvc.state = TeleworkerState.UnavailableIB;
                this.workSvc.showStateModal();
              } else if (currentState === TeleworkerState.UAT) {
                this.workSvc.getNextUATRecord();
              } else if (voip && !this.nailledin) {
                this.coreSvc.state = TeleworkerState.UnavailableIB;
                this.workSvc.showStateModal();
              }

              /// if we are still on the call we don't do anything here as the CallDisconnected event will sort it all out.
              /// this is why we don't have an else.
            }
          );
      }

      //if (this.manualOB) {
      //  this.coreSvc.state = TeleworkerState.Work;
      //  this.manualOB = false;
      //  this.workSvc.refreshScheduledCampaigns();
      // }
    }
  }

  private initCoreStateListener() {
    this.coreSvc.state$.subscribe((twstate) => {
      if (
        !!this.pendingStopRecording &&
        this.onCallStates.includes(twstate!) &&
        twstate !== TeleworkerState.CallDialling
      ) {
        this.signalrSend(this.pendingStopRecording);
        this.pendingStopRecording = undefined;
      }

      switch (twstate) {
        case TeleworkerState.DiallerLogin:
          // first we need to check to see if we are currently nailed up
          this.calltaken = false;

          if (this.ib == true) {
            // if IB then we send 0 as the campaign to force the connection to be to the user.
            this.login(0);
            if (!this.voip) this.workSvc.openNailupModal();
          } else {
            if (this.campaignid) {
              this.login(this.campaignid);
              if (!this.voip) this.workSvc.openNailupModal();
            }
          }

          break;
        case TeleworkerState.InboundConnected:
        case TeleworkerState.OutboundConnected:
          this.calltaken = true;
          this.ringing = false;
          break;
        case TeleworkerState.Voicemail:
          this.onVoicemailCall = true;
          break;
        case TeleworkerState.Work:
          this.onVoicemailCall = false;
          break;
        case TeleworkerState.BlendedPreview:
          // this is where we have a user with IB and OB Preview campaigns scheduled
          // and they are switching to OB Preview
          if (this.nailledin) {
            // guess we were on an IB campaign and are moving to OB
            // shouldn't need to do CancelGetNextNumber() as we will already be in Unavailable state.
            this.cancelGetNextNumber();
            this.workSvc.getPreviewRecord(this.campaignid);
          } else {
            // login to trigger nail up I guess.
            if (this.campaignid) {
              //this.login(this.campaignid);
              // we need to login as if it was IB so we can just switch across to the IB campaign (by doing getNextNumber())
              this.login(0);
              if (!this.voip) this.workSvc.openNailupModal();
            }
          }
          break;
        case TeleworkerState.DiallerLogout:
          this.nailOutAndLogout();
          this.campaignid = 0;
          // just in case we are retrying the voip connection and are exiting
          // we want to clear the timeout.
          //if (this.voip) this.sipSvc.disconnectPhone(); //this is not nessecary as we are now using the reconnecting flag to determine if we need to nailup again.
          break;
        case TeleworkerState.Ringing:
          this.ringing = true;
          this.calltaken = false;
          break;
        case TeleworkerState.Unavailable:
          break;
        case TeleworkerState.SIPNailIn:
          setTimeout(() => this.nailedup(), 2000);
          break;
        case TeleworkerState.EmptyQueue:
          this.coreSvc.state = TeleworkerState.UnavailableOB;
          // set this to false so that we can't launch another campaign.
          this.workSvc.inUnavailableState = false;
          this.workSvc.retryEmptyQueue();
          this.retryEmptyQueueTimer = window.setTimeout(
            () => this.processNailedUp(),
            10000
          );

          break;
        case TeleworkerState.CallDisconnected:
          this.logger.info(
            'DiallerService',
            'initCoreStateListener',
            'Call Disconnected Received'
          )();
          if (this.calltaken) {
            this.workSvc.setWrapStart();
          }

          if (
            this.pendingNaiupModal &&
            !this.voip &&
            this.workSvc.scriptClosed
          ) {
            this.pendingNaiupModal = false;
            // show the prompt for the nailup timeout
            this.coreSvc.state = TeleworkerState.Work;
            this.workSvc.openNailupTimeoutModal();
          } else {
            if (this.ib && !this.manualOB) {
              // if script has been closed then do getNextNumber() and move into Available IB
              // else move to Wrap IB.

              // and wait for standbylimit seconds until moving back into the relevant state
              this.workSvc.standbyLimit$
                .pipe(
                  withLatestFrom(
                    this.nailuptime$,
                    this.workSvc.nailupTimeLimit$
                  ),
                  take(1)
                )
                .subscribe(([standbylimit, nailupstart, nailuplimit]) => {
                  if (this.workSvc.scriptClosed && this.calltaken) {
                    let elapsedtimeminutes = (Date.now() - nailupstart) / 60000;
                    if (elapsedtimeminutes >= nailuplimit! && !this.voip) {
                      // we need to hangup the nailup call.
                      if (this.nailledin) this.nailOutAndLogout();
                      this.coreSvc.state = TeleworkerState.Work;
                      this.workSvc.openNailupTimeoutModal();
                    } else {
                      this.workSvc
                        .getAgentStatus$(this.hostname, this.port)
                        .subscribe((agentstatus) => {
                          this.logger.info(
                            'DiallerService',
                            'getAgentStatus$',
                            `Agent Status: ${agentstatus.status}`
                          )();
                          switch (agentstatus.status) {
                            case 'Disconnected':
                              this.nailOutAndLogout();
                              this.coreSvc.state = TeleworkerState.Work;
                              break;
                            case 'Unavailable':
                            case 'Available':
                              this.coreSvc.state = TeleworkerState.Standby;

                              setTimeout(
                                () => this.moveFromStandby(),
                                standbylimit * 1000
                              );
                              break;
                          }
                        });
                    }
                  } else {
                    let elapsedtimeminutes = (Date.now() - nailupstart) / 60000;
                    if (elapsedtimeminutes >= nailuplimit! && !this.voip) {
                      // we need to hangup the nailup call.
                      if (this.nailledin) this.nailOutAndLogout();
                    }

                    if (!this.calltaken && this.ringing) {
                      this.coreSvc.state = TeleworkerState.IBMissedCall;
                    } else {
                      this.coreSvc.state = TeleworkerState.WrapIB;
                    }
                  }
                });
            } else {
              this.coreSvc.state = TeleworkerState.WrapOB;

              // we have made a manual OB call so check to see if we have a recordid
              this.workSvc.master$.pipe(take(1)).subscribe((master) => {
                if (
                  !master ||
                  master.recordId === undefined ||
                  master.recordId <= 0
                ) {
                  this.workSvc.submitEmptyRecordRecording();
                }
              });
            }
          }
          //}
          this.calltaken = false;

          break;
      }
    });

    navigator.serviceWorker.register('/sw.js', { scope: './' });
    fromEvent(navigator.serviceWorker, 'message')
      .pipe(map((event) => event as MessageEvent))
      .subscribe((message) => {
        if (message.origin != 'https://localhost:5000')
          this.logger.debug('DiallerService', 'onMessage', {
            origin: message.origin,
            data: message.data,
          })();
        this.notifyClose();
        if (message.data.type == 'notify' && message.data.action === 'answer') {
          this.logger.debug('DiallerService', 'onMessage', 'Answering')();
          this.inboundConnect();
        }
      });

    this.coreSvc.state$
      .pipe(withLatestFrom(this.workSvc.autoAnswer$))
      .subscribe(([twstate, autoAnswer]) => {
        switch (twstate) {
          case TeleworkerState.Ringing:
            this.notifyRinging();
            break;
          case TeleworkerState.InboundConnected:
            if (autoAnswer) {
              this.notifyAnswered();
            } else {
              this.notifyClose();
            }
            break;
          default:
            this.notifyClose();
            break;
        }
      });
  }

  private initOpenTasksIfAvailableListener() {
    this.coreSvc.state$
      .pipe(
        switchMap((state) =>
          state == TeleworkerState.AvailableIB
            ? this.callbackSvc.callbacksDue$.pipe(
                filter((callbacksDue) => callbacksDue === true),
                delay(5000),
                take(1)
              )
            : of(false)
        ),
        filter((callbacksDue) => callbacksDue === true)
      )
      .subscribe(() => {
        if (!this.calltaken) {
          this.logger.info(
            'DiallerService',
            'initOpenTasksIfAvailableListener'
          )();
          this.cancelGetNextNumber();
          this.workSvc.openTasksModal();
        }
      });
  }

  private initTransferCallListener() {
    this.workSvc.transferCall$
      .pipe(
        filter((tc) => !!tc),
        map((tc) => tc!)
      )
      .subscribe((transferCall) => {
        const {
          thirdPartyNumber,
          options: { agent3Way, stopRecording },
        } = transferCall;
        this.xferStopRecording = stopRecording;

        if (this.calltaken == false) {
          this.callManual(thirdPartyNumber, '', 30);
        } else {
          if (agent3Way) {
            this.agent3Way(thirdPartyNumber, '', 30000);
          } else {
            if (this.xferStopRecording) this.stopRecording();
            this.transferToAgent(thirdPartyNumber, '', '');
          }
        }
      });
  }

  private initVariables() {
    this.sipSvc.voip$.subscribe((voip) => {
      this.voip = voip;
    });

    this.sipSvc.nailedIn$
      .pipe(withLatestFrom(this.sipSvc.nailInCallLost$, this.coreSvc.state$))
      .subscribe(([nailledin, reason, state]) => {
        if (this.nailledin && !nailledin) {
          // we have been nailed out unexpectedly
          this.logger.error('DiallerService', 'nailInCallLost$', {
            reason,
            state,
          })();
          this.toastr.error(
            'Network Connectivity Failure',
            'Nail In Call Disconnected',
            {
              disableTimeOut: true,
              closeButton: true,
            }
          );
          //prevent a re-nailin from doing the normal post-nailin actions
          this.nailInCallLostState = state;
          switch (state) {
            case TeleworkerState.AvailableIB:
            case TeleworkerState.Ringing:
              this.coreSvc.state = TeleworkerState.UnavailableIB;
              this.workSvc.showStateModal();
              break;
            case TeleworkerState.AvailableOB:
              this.coreSvc.state = TeleworkerState.UnavailableOB;
              this.workSvc.showStateModal();
              break;
            case TeleworkerState.InboundConnected:
            case TeleworkerState.HoldIB:
            case TeleworkerState.TransferIB:
              this.hangUp();
              this.coreSvc.state = TeleworkerState.WrapIB;
              break;
            case TeleworkerState.OutboundConnected:
            case TeleworkerState.HoldOB:
            case TeleworkerState.TransferOB:
            case TeleworkerState.CallDialling:
              this.hangUp();
              this.coreSvc.state = TeleworkerState.WrapOB;
              break;
            case TeleworkerState.Unavailable:
              //prevent already unavailable - allow normal post-nailin actions
              this.nailInCallLostState = undefined;
              if (this.ib) {
                //prevent auto-renailin & going availableib
                this.ib = false;
                this.campaignid = 0;
                this.nailOutAndLogout();
                //this.workSvc.openNailupDroppedModal();
              }
              break;
            default:
              //some other, possibly on an active call, state (Preview, Break, Wrap, etc)
              break;
          }

          if (!!this.callManualPending) {
            this.callManualPending = undefined;
            this.toastr.warning(
              'Network Connectivity Failure',
              'OB Call Cancelled'
            );
          }
        }

        this.nailledin = nailledin;
      });

    this.sipSvc.registered$
      .pipe(distinctUntilChanged(), withLatestFrom(this.coreSvc.state$))
      .subscribe(([registered, state]) => {
        this.logger.info('DiallerService', 'SIP registred', registered)();
        this.sipRegistered = registered;
        if (
          registered &&
          !this.nailledin &&
          state !== TeleworkerState.Unavailable &&
          state !== TeleworkerState.UnavailableIB &&
          state !== TeleworkerState.UnavailableOB
        ) {
          // SIP Client connected & not nailed in already (if websocket disconnect only, not udp) - we should nailin now
          this.nailIn();
        } else {
          // SIP Client disconnected or already unavailable - do not nail in
        }
      });

    this.workSvc.hasIBScheduled$.subscribe((ib) => {
      this.ib = ib === undefined ? false : ib;
      this.hasIBScheduled = ib === undefined ? false : ib;
    });

    this.workSvc.obLaunched$
      .pipe(withLatestFrom(this.workSvc.hasIBScheduled$))
      .subscribe(([obLaunched, hasIBScheduled]) => {
        if (obLaunched) {
          this.ib = false;
        } else {
          this.ib = hasIBScheduled;
        }
      });

    /*this.workSvc.postCallSurveyData$
      //.pipe(filter((pcs) => !!pcs))
      .subscribe((pcs) => {
        if (pcs !== undefined) this.postCallSurvey(pcs);
      });

    this.workSvc.postCallSurveyCancel$.subscribe((cancel) => {
      if (cancel !== undefined && cancel === true) this.cancelTransfer();
    }); */

    this.workSvc.campaignDialType$.subscribe((dialType) => {
      this.dialType = dialType;
    });

    this.coreSvc.campaignId$.subscribe((c) => {
      if (c && c != 0 && c != this.campaignid) {
        // IB so isn't triggered from the campaign selection change but the Availiable button click.
        if (this.ib == false) {
          this.campaignid = c;
        }
      }
    });
  }

  private login(campaignId: number): void {
    this.signalrSend(new LoginRequest({ campaignId }));
  }

  private moveFromStandby() {
    this.coreSvc.state$.pipe(take(1)).subscribe((currentState) => {
      // only execute this is still in standby (as they may have gone into Unavailable in the interim)
      if (currentState === TeleworkerState.Standby) {
        if (this.workSvc.scriptClosed === true) {
          this.getNextNumber();
          this.coreSvc.state = TeleworkerState.AvailableIB;
        } else {
          this.coreSvc.state = TeleworkerState.WrapIB;
        }
      }
    });
  }

  private moveToIBAvailable() {
    this.coreSvc.state$.pipe(take(1)).subscribe((currentState) => {
      // only change state if still in standby.
      if (currentState === TeleworkerState.Standby) {
        if (this.ibxferonly === true) {
          // we have just preocessed a transfer when we were in IB Transfers only state
          // so reset the queues to the correct IB queues
          this.ibxferonly = false;
          this.resetIBQueues();
        } else {
          this.getNextNumber();
          this.coreSvc.state = TeleworkerState.AvailableIB;
        }
      }
    });
  }

  private moveToIBAvailableAfterReset() {
    // we have just reset the IB queues so call getNextNumber and update state.
    this.getNextNumber();
    this.coreSvc.state = TeleworkerState.AvailableIB;
  }

  private nailIn(): void {
    var hostname: string = this.hostname;
    var port: number = this.port;
    this.pendingNaiupModal = false;
    if (!!hostname && !!port) {
      this.signalrSend(new NailInRequest({ hostname, port }));
      clearTimeout(this.nailInTimeout);
      if (this.voip) {
        this.nailInTimeout = setTimeout(() => {
          this.logger.error('DiallerService', 'nailIn', 'Nail In Timeout')();
          this.toastr.error('Nail In Timeout', 'Nail In Error', {
            disableTimeOut: true,
            closeButton: true,
          });
          this.workSvc.updateStateToGeneral();
        }, 10 * 1000);
      }
    } else {
      this.logger.error(
        'DiallerService',
        'nailIn',
        'Hostname or Port not set'
      )();
      this.toastr.error('Hostname or Port not set', 'Nail In Error');
      this.coreSvc.state = TeleworkerState.Work;
    }
  }

  private notifyAnswered() {
    this.logger.debug('DiallerService', 'notifyAnswered', 'Notifying')();
    this.serviceWorker$.pipe(take(1)).subscribe((reg) =>
      reg!.showNotification('Unity4 Connect', {
        body: 'AutoAnswered Inbound Call',
        icon: 'assets/images/favicon/android-icon-192x192.png',
        requireInteraction: false,
      })
    );
  }

  private notifyClose() {
    //this.logger.debug('DiallerService', 'notifyClose', 'Closing')();
    this.ringingStop();
    this.serviceWorker$
      .pipe(take(1))
      .subscribe((reg) =>
        reg
          ?.getNotifications()
          .then((nots) => nots.forEach((not) => not.close()))
      );
  }

  private notifyRinging() {
    this.logger.debug('DiallerService', 'notifyRinging', 'Notifying')();
    this.ringingPlay();
    this.serviceWorker$.pipe(take(1)).subscribe((reg) =>
      reg!.showNotification('Unity4 Connect', {
        body: 'Incoming Inbound Call',
        icon: 'assets/images/favicon/android-icon-192x192.png',
        requireInteraction: true,
        actions: [
          {
            action: 'answer',
            title: 'Answer',
          },
        ],
      } as any)
    );
  }

  private onAgent(event: AgentEvent) {
    //update application state here
  }

  private onConnect(event: ConnectEvent) {
    this.workSvc.setNumberUsed(event.dialledNumber);
    if (!this.ib) {
      // script is already available and record is populated
      this.workSvc.routeToScript(event.recording);
      this.coreSvc.state = TeleworkerState.OutboundConnected;
      this.workSvc.setContactStart();
    } else {
      this.manualOB = true;
      if (!this.calltaken && this.xferStopRecording) {
        //callManual initiated with stop recording selected in advance
        this.stopRecording();
      } else {
        //transfer call
        this.workSvc.setAgent3WayState('Successful');
        this.workSvc.addRecording(event.recording); //stop record occurs upon hangup
      }
      this.workSvc.setContactStart();
      this.coreSvc.state = TeleworkerState.OutboundConnected;
    }
  }

  private onDialling(event: DiallingEvent) {
    if (this.ib) this.manualOB = true;
    else this.manualOB = false;

    this.coreSvc.state = TeleworkerState.CallDialling;
  }

  private onDisconnected(event: DisconnectedEvent) {
    this.socketOpen = false;
    this.coreSvc.state = TeleworkerState.CallDisconnected;
    this.pendingStopRecording = undefined; //any pending stop recording commands are now irrelevant as the call has disconnected
    this.disconnected$.next();
  }

  private onError(event: ErrorEvent) {
    if (event.error != '') {
      if (event.error === 'Originate successfully queued') {
        this.toastr.info('Call Proceeding');
      } else if (event.error === 'Get Next Number Cancelled') {
        this.socketOpen = false;
        // this.toastr.info(event.error);
      } else if (event.error === 'Power queue is empty') {
        this.socketOpen = false;
        this.logger.error('DiallerService', 'Dialler Error', { event })();
        this.toastr.error(event.error, 'Dialler Error');
        this.coreSvc.state = TeleworkerState.EmptyQueue;
      } else if (
        event.error ===
          'Your phone is not logged in, please check to make sure you hear music' ||
        event.error === 'Agent phone no longer logged in'
      ) {
        this.logger.error('DiallerService', 'Dialler Error', {
          event,
          nailledin: this.nailledin,
        })();
        this.toastr.error(event.error, 'Dialler Error');
        if (this.nailledin) {
          this.nailledin = false;
          this.workSvc.setUnavailableToTakeCalls();
          //if (this.ib) this.availableib();

          this.coreSvc.state$.pipe(take(1)).subscribe((state) => {
            this.nailInCallLostState = state;
            switch (state) {
              case TeleworkerState.AvailableIB:
              case TeleworkerState.Ringing:
                this.coreSvc.state = TeleworkerState.UnavailableIB;
                this.workSvc.showStateModal();
                break;
              case TeleworkerState.AvailableOB:
                this.coreSvc.state = TeleworkerState.UnavailableOB;
                this.workSvc.showStateModal();
                break;
              case TeleworkerState.InboundConnected:
              case TeleworkerState.HoldIB:
              case TeleworkerState.TransferIB:
                this.hangUp();
                this.coreSvc.state = TeleworkerState.WrapIB;
                break;
              case TeleworkerState.OutboundConnected:
              case TeleworkerState.HoldOB:
              case TeleworkerState.TransferOB:
              case TeleworkerState.CallDialling:
                this.hangUp();
                this.coreSvc.state = TeleworkerState.WrapOB;
                break;
              case TeleworkerState.Unavailable:
                //prevent already unavailable - allow normal post-nailin actions
                this.nailInCallLostState = undefined;
                break;
              default:
                //some other, possibly on an active call, state (Preview, Break, Wrap, etc)
                break;
            }
          });
        }
      } else {
        this.logger.error('DiallerService', 'Dialler Error', { event })();
        this.toastr.error(event.error, 'Dialler Error');
      }
    }
  }

  private onIBTransferOnly(event: IBTransferOnlyEvent) {
    this.ibxferonly = true;
    this.coreSvc.state = TeleworkerState.IBTransferOnly;
    this.getNextNumber();
  }

  private onInboundConnect(event: InboundConnectEvent) {
    /*this.workSvc.scheduledCampaigns$.pipe(take(1)).subscribe((campaigns) => {
      let ibCampaignId =
        campaigns?.find((c) => c.dnisList?.includes(event.dnis))?.campaignId ||
        0;
      if (ibCampaignId === 0 && event.dnis.length < 8)
        ibCampaignId = +event.dnis;

      this.workSvc.popScript(
        ibCampaignId,
        0,
        event.dnis,
        event.campaignId, // this has the rapport queueid, queueid has the telephony id (1-99)
        event.recording,
        event.extraData,
        Number(event.waitTime)
      );

      this.coreSvc.state = TeleworkerState.InboundConnected;
    }); */
    this.workSvc.addRecording(event.recording);
    this.workSvc.setContactDtStart(Number(event.waitTime));
    this.pendingNaiupModal = false;
    this.coreSvc.state = TeleworkerState.InboundConnected;
  }

  private onInboundWaiting(event: InboundWaitingEvent) {
    this.workSvc.autoAnswer$
      .pipe(withLatestFrom(this.workSvc.scheduledCampaigns$), take(1))
      .subscribe(([autoAnswer, campaigns]) => {
        let dnis = event.leadId; // leadId contains the DNIS for IB
        let ibCampaignId =
          campaigns?.find((c) => c.dnisList?.includes(dnis))?.campaignId || 0;
        if (ibCampaignId === 0 && dnis.length < 8) ibCampaignId = +dnis;

        this.pendingPopScriptDetails = {
          campaignId: ibCampaignId,
          recordId: 0,
          DNIS: dnis,
          queueId: event.campaignId, // this has the rapport queueid, queueid has the telephony id (1-99)
          recording: '',
          extraData: event.extraData,
          waitTime: 0,
        };

        if (autoAnswer == true) {
          this.inboundConnect();
        } else {
          this.coreSvc.state = TeleworkerState.Ringing;
        }
      });
  }

  private onLoggedIn(event: LoggedInEvent) {
    this.hostname = event.hostname;
    this.port = event.port;
    // so we should nailin now if not voip
    this.coreSvc.state = TeleworkerState.DiallerLoggedIn;

    if (!this.voip) {
      this.nailIn();
    } else {
      if (this.sipRegistered) {
        // SIP client already connected so nailup
        this.nailIn();
      }
      // start the initialisation process for the SIP client
      else {
        this.sipSvc.connectPhone();
      }
    }
  }

  private onPing(event: PingEvent) {
    //update application state here
  }

  private onPowConnect(event: PowConnectEvent) {
    this.workSvc.storeOBCall(
      this.campaignid!,
      +event.recordId,
      'OB',
      event.queueId,
      event.recording,
      event.extraData
    );

    this.coreSvc.recordId = +event.recordId;
    this.coreSvc.state = TeleworkerState.OutboundConnected;
  }

  private onResetIBQueues(event: ResetIBQueuesEvent) {
    // move into Standby State
    this.coreSvc.state = TeleworkerState.Standby;

    // and then set a timer to move into Available IB
    this.workSvc.standbyLimit$.pipe(take(1)).subscribe((standbylimit) => {
      setTimeout(() => this.moveToIBAvailableAfterReset(), standbylimit * 1000);
    });
  }

  private onWaiting(event: WaitingEvent) {
    this.coreSvc.state = TeleworkerState.OutboundWaiting;
  }

  private processNailedUp() {
    if (!this.ib) {
      this.callbackSvc.callbacksDue$
        .pipe(
          withLatestFrom(this.workSvc.campaignId$),
          switchMap(([callbacksDue, campaignId]) =>
            this.workSvc.isUserScheduled(campaignId!).pipe(
              map((isSched) => ({
                callbacksDue,
                campaignId,
                isSched,
              }))
            )
          ),
          take(1)
        )
        .subscribe(({ callbacksDue, campaignId, isSched }) => {
          if (!isSched) {
            this.logger.info(
              'DiallerService',
              'processNailedUp() not isSched'
            )();
            this.toastr.info('Shift Ended');
            this.workSvc.exit();
            this.workSvc.navigateToWork();
            this.workSvc.unsetWork();
            this.coreSvc.state = TeleworkerState.UnavailableIB;
            this.workSvc.showStateModal();
          } else {
            if (callbacksDue) {
              this.workSvc.openTasksModal();
            } else {
              if (this.dialType == 'Predictive') {
                // OB predictive command we need to just go available (run getnextnumber) straight away

                this.coreSvc.state = TeleworkerState.AvailableOB;
                this.workSvc.setWaitingForOB(true);
                this.getNextNumber();
              } else if (this.dialType == 'Preview') {
                this.workSvc.getPreviewRecord(campaignId!);
              }
            }
          }
        });
    } else {
      if (this.manualOB) {
        this.coreSvc.state = TeleworkerState.Work;
        this.manualOB = false;
        this.workSvc.reloadCampaigns();
      } else {
        this.moveToIBAvailable();
      }
    }
  }

  private reconnectNailin() {
    if (!this.voip) this.workSvc.openNailupModal();

    if (this.ib == true) {
      // if IB then we send 0 as the campaign to force the connection to be to the user.
      this.login(0);
    } else if (this.campaignid) {
      this.login(this.campaignid);
    }
  }

  private resetIBQueues(): void {
    // this will update the queues to the relevant queues for the user.
    // dependant on already being logged in (and nailled up)
    this.signalrSend(new ResetIBQueuesRequest());
  }

  private ringingPlay() {
    this.ringingAudio$.pipe(take(1)).subscribe((audioElement) => {
      audioElement.play();
    });
  }

  private ringingStop() {
    this.ringingAudio$.pipe(take(1)).subscribe((audioElement) => {
      audioElement.pause();
    });
  }

  private setCallManualPending(
    phoneNumber: string,
    callerId: string,
    timeout: number
  ) {
    if (this.callManualPending) {
      this.toastr.warning('Existing call already pending', 'OB Call Cancelled');
      return;
    }
    clearTimeout(this.callManualPendingTimeout);
    this.callManualPending = { phoneNumber, callerId, timeout };
    this.callManualPendingTimeout = setTimeout(() => {
      if (this.callManualPending) {
        this.callManualPending = undefined;
        this.toastr.warning('Unable to NailIn', 'OB Call Cancelled');
      }
    }, 15 * 1000);
  }

  private signalrInit() {
    const retryTimes = [
      1000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
    ];
    const hubConn = new HubConnectionBuilder()
      .withUrl(environment.diallerHub, {
        accessTokenFactory: () => this.authSvc.accessToken || '',
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (context) => {
          this.logger.warn(
            'DiallerService',
            'hubConn.withAutomaticReconnect',
            context
          )();
          if (context.previousRetryCount < retryTimes.length) {
            return retryTimes[context.previousRetryCount];
          }
          return null;
        },
      })
      .configureLogging(LogLevel.Warning)
      .build();
    this.hubConn = hubConn;
    hubConn.onclose((error: Error | undefined) => {
      this.hubStateSub.next(hubConn.state);
      this.logger.error('DiallerService', 'hubConn.onclose', error)();
      this.signalrRestart();
    });

    hubConn.onreconnecting(() => this.hubStateSub.next(hubConn.state));
    hubConn.onreconnected(() => this.hubStateSub.next(hubConn.state));
    this.authSvc.isLoggedIn$
      .pipe(
        filter((isLoggedIn) => !!isLoggedIn),
        take(1)
      )
      .subscribe(() => this.signalrStart());
    return hubConn;
  }

  private signalrRestart(): void {
    this.signalrRetries++;
    if (this.signalrRetries < 20) {
      // for the initial retries - schedule for every 30 seconds
      setTimeout(() => this.signalrStart(), 30000);
    } else {
      // then wait a further 10 minutes before trying the process again.
      this.signalrRetries = 0;
      setTimeout(() => this.signalrStart(), 600000);
    }
  }

  private signalrSend(request: DiallerSignalRRequests) {
    this.connected$.pipe(take(1)).subscribe(() => {
      if (request.payload === undefined) {
        this.logger.info('DiallerService', `SEND ${request.name}`, request)();
        this.hubConn.send(request.name);
      } else {
        if (
          request.name != 'Login' &&
          (!request.payload.hostname || !request.payload.port)
        ) {
          this.logger.error(
            'DiallerService',
            `SEND ${request.name} missing hostname`,
            request
          )();
          return;
        }
        this.logger.info('DiallerService', `SEND ${request.name}`, request)();
        this.hubConn.send(request.name, request.payload);
      }
    });
  }

  private signalrStart(): void {
    this.hubConn
      .start()
      .then(() => {
        this.signalrRetries = 0;
        this.logger.debug(
          'DiallerService',
          'hubConn.start',
          this.hubConn.state
        )();
        this.hubConn.off('agent');
        this.hubConn.on('agent', (event) => this.onAgent(event));
        this.hubConn.off('dialling');
        this.hubConn.on('dialling', (event) => this.onDialling(event));
        this.hubConn.off('disconnected');
        this.hubConn.on('disconnected', (event) => this.onDisconnected(event));
        this.hubConn.off('error');
        this.hubConn.on('error', (event) => this.onError(event));
        this.hubConn.off('inboundConnect');
        this.hubConn.on('inboundConnect', (event) =>
          this.onInboundConnect(event)
        );
        this.hubConn.off('inboundWaiting');
        this.hubConn.on('inboundWaiting', (event) =>
          this.onInboundWaiting(event)
        );
        this.hubConn.off('loggedIn');
        this.hubConn.on('loggedIn', (event) => this.onLoggedIn(event));
        this.hubConn.off('ping');
        this.hubConn.on('ping', (event) => this.onPing(event));
        this.hubConn.off('connect');
        this.hubConn.on('connect', (event) => this.onConnect(event));
        this.hubConn.off('powConnect');
        this.hubConn.on('powConnect', (event) => this.onPowConnect(event));
        this.hubConn.off('waiting');
        this.hubConn.on('waiting', (event) => this.onWaiting(event));
        this.hubConn.off('ibTransferOnly');
        this.hubConn.on('ibTransferOnly', (event) =>
          this.onIBTransferOnly(event)
        );
        this.hubConn.off('resetIBQueues');
        this.hubConn.on('resetIBQueues', (event) =>
          this.onResetIBQueues(event)
        );
      })
      .catch((err) => {
        this.logger.error('DiallerService', 'hubConn.start', err)();
        this.signalrRestart();
      })
      .finally(() => {
        this.hubStateSub.next(this.hubConn.state);
      });
  }

  private unattendedTransfer(
    thirdPartyNumber: string,
    callerId: string,
    timeout: number
  ): void {
    var hostname: string = this.hostname;
    var port: number = this.port;

    this.signalrSend(
      new UnattendedTransferRequest({
        thirdPartyNumber,
        callerId,
        timeout,
        hostname,
        port,
      })
    );

    // unattended so just move straight into wrap
    if (this.ib && !this.manualOB && !this.onVoicemailCall)
      this.coreSvc.state = TeleworkerState.WrapIB;
    else this.coreSvc.state = TeleworkerState.WrapOB;
  }

  private updateStateAfterTransferCancel(newState: TeleworkerState) {
    this.coreSvc.state$.pipe(take(1)).subscribe((currState) => {
      // if we are in Wrap then the caller has hungup - stay in Wrap.
      if (
        currState !== TeleworkerState.WrapIB &&
        currState !== TeleworkerState.WrapOB
      ) {
        this.coreSvc.state = newState;
      }
    });
  }

  agent3Way(thirdPartyNumber: string, callerId: string, timeout: number): void {
    var hostname: string = this.hostname;
    var port: number = this.port;

    this.workSvc.setAgent3WayState('Initiated');
    this.agent3WayPending = true;

    this.signalrSend(
      new Agent3WayRequest({
        thirdPartyNumber,
        callerId,
        timeout,
        hostname,
        port,
      })
    );

    // move into relevant Transfer presence
    // clicking hangup (to leave caller and third party connected)
    // will initiate move into Wrap state.
    if (this.ib && !this.onVoicemailCall)
      this.coreSvc.state = TeleworkerState.TransferIB;
    else this.coreSvc.state = TeleworkerState.TransferOB;
  }

  availableib(): void {
    // first check to see if we are currenly logged in/nailled up
    // if we are then we need to just go Available I guess
    this.manualOB = false;
    this.workSvc.updateIbLaunched(true);

    if (this.nailledin) {
      if (this.ib == true) {
        if (this.ibxferonly == true) {
          this.resetIBQueues();
        } else {
          this.coreSvc.state = TeleworkerState.Standby;

          this.workSvc.standbyLimit$.subscribe((standbyLimit) => {
            this.workSvc.navigateToWork();
            setTimeout(() => this.moveToIBAvailable(), standbyLimit * 1000);
          });

          //blended campaigns (closing off OB) can come through this brnach, so need to reset the dial type.
          this.coreSvc.campaignId = 0;
        }
      } else {
        // we are in an OB campaign currently but are closing
        // and we have an IB campaign scheduled so
        // move to STandby as if we have just gone available IB
        this.coreSvc.state = TeleworkerState.Standby;
        this.workSvc.standbyLimit$.subscribe((standbyLimit) => {
          setTimeout(() => this.moveToIBAvailable(), standbyLimit * 1000);
        });

        this.coreSvc.campaignId = 0;
        this.ib = true;
        // and navigate to work.
        this.workSvc.navigateToWork();
      }
    } else {
      this.schedChkSvc.lateCheck(0).subscribe(() => {
        this.coreSvc.campaignId = 0;
        this.ib = true;
        this.login(0); // IB so campaignid = 0
        this.workSvc.setNailupTimeLimitIB();

        if (!this.voip) this.workSvc.openNailupModal();
      });
    }
  }

  callManual(phoneNumber: string, callerId: string, timeout: number): void {
    if (!phoneNumber || !phoneNumberRegExp.test(phoneNumber)) {
      this.toastr.error(
        'Phone number must be greater than 6 digits (0-9)',
        'Invalid Number',
        {
          timeOut: 10000,
        }
      );
      return;
    }

    //check to see if we have a campaign selected...we need one
    this.coreSvc.campaignId$
      .pipe(
        take(1),
        withLatestFrom(this.authSvc.userId$, this.workSvc.scheduledCampaigns$)
      )
      .subscribe(([campaignId, userId, schedcampaigns]) => {
        let selectedCampaignId = campaignId;
        if (!selectedCampaignId || selectedCampaignId < 1) {
          // if we have only 1 IB then use that one.
          const ibCampaigns = schedcampaigns?.filter((camp) =>
            camp.actionType.startsWith('IB')
          );
          if (ibCampaigns?.length === 1) {
            selectedCampaignId = ibCampaigns[0].campaignId;
            this.workSvc.selectCampaign(selectedCampaignId);
          }
        }

        if (!selectedCampaignId || selectedCampaignId < 1) {
          // no campaign selected
          this.toastr.info(
            'You must select a campaign first.',
            'Campaign Selection',
            {
              timeOut: 10000,
            }
          );
          return;
        }

        if (!this.hostname) {
          this.setCallManualPending(phoneNumber, callerId, timeout);
          this.coreSvc.state = TeleworkerState.DiallerLogin;
          return;
        }

        if (!this.nailledin) {
          this.setCallManualPending(phoneNumber, callerId, timeout);
          if (this.voip) {
            if (this.sipRegistered) {
              // SIP client already connected so nailup
              this.nailIn();
            }
            // start the initialisation process for the SIP client
            else {
              this.sipSvc.connectPhone();
            }
          } else {
            this.nailIn();
          }
          return;
        }

        var clistring: string =
          userId?.toString() +
          '||' +
          selectedCampaignId.toString() +
          '|' +
          callerId;

        this.socketOpen = true;
        this.signalrSend(
          new CallManualRequest({
            leadId: phoneNumber, //set as phonenumber so that it's returned in Connect event's dialledNumber (leadId)
            phoneNumber,
            callerId: clistring,
            timeout,
            hostname: this.hostname,
            port: this.port,
          })
        );
        this.coreSvc.state = TeleworkerState.CallDialling;
      });
  }

  cancelGetNextNumber(): void {
    var hostname: string = this.hostname;
    var port: number = this.port;

    if (this.nailledin) {
      this.coreSvc.state$.pipe(take(1)).subscribe((currentstate) => {
        if (currentstate !== TeleworkerState.Standby)
          this.signalrSend(new CancelGetNextNumberRequest({ hostname, port }));
      });
    }

    if (this.ib) this.coreSvc.state = TeleworkerState.UnavailableIB;
    else this.coreSvc.state = TeleworkerState.UnavailableOB;
  }

  cancelTransfer(onVoicemailCall: boolean = false): void {
    let leadId: string = '';
    this.workSvc.setAgent3WayState('Cancelled');
    this.agent3WayPending = false;
    this.coreSvc.campaignId$
      .pipe(
        take(1),
        withLatestFrom(this.workSvc.master$, this.workSvc.scheduledCampaigns$)
      )
      .subscribe(([campaignId, master, schedcampaigns]) => {
        let selectedCampaignId = campaignId;
        if (!selectedCampaignId || selectedCampaignId < 1) {
          // if we have only 1 IB then use that one.
          const ibCampaigns = schedcampaigns?.filter((camp) =>
            camp.actionType.startsWith('IB')
          );
          if (ibCampaigns?.length === 1) {
            selectedCampaignId = ibCampaigns[0].campaignId;
            this.workSvc.selectCampaign(selectedCampaignId);
          }
        }

        if (leadId === '' || leadId === '0') {
          if (
            !this.ib &&
            master !== undefined &&
            master.recordId !== undefined
          ) {
            leadId = master.recordId.toString();
          } else {
            leadId = selectedCampaignId!.toString();
          }

          this.transferToAgent('0', leadId, '0');

          if (this.ib == true && !onVoicemailCall) {
            setTimeout(
              () =>
                this.updateStateAfterTransferCancel(
                  TeleworkerState.InboundConnected
                ),
              1000
            );
          } else {
            setTimeout(
              () =>
                this.updateStateAfterTransferCancel(
                  TeleworkerState.OutboundConnected
                ),
              1000
            );
          }
        }
      });
  }

  clearRetryEmptyQueue() {
    if (this.retryEmptyQueueTimer) {
      clearTimeout(this.retryEmptyQueueTimer);
    }
  }

  closeIB() {
    this.pendingStopRecording = undefined;

    this.submitting.next(true);
    this.workSvc.outcome$.pipe(take(1)).subscribe((outcome: any) => {
      if (outcome !== undefined) {
        // first log the record
        this.workSvc
          .submitRecord()
          .pipe(
            withLatestFrom(this.workSvc.master$),
            tap(([submitted, master]) =>
              this.callbackSvc.updateCallback(master?.recordId)
            )
          )
          .subscribe(
            () => {
              // after we submit we need to close the record
              this.workSvc.closeRecord();
              this.workSvc.loadNNData();
              // and now call scriptClosed as this will put them in the appropriate Available state and
              // do getNextNumber ready for the next call.
              this.finaliseRecord();
              this.submitting.next(false);
              // and navigate to work.
              this.workSvc.navigateToWork();
            },
            (err) => this.submitting.next(false)
          );
      } else {
        this.submitting.next(false);
        this.toastr.error(
          'No outcome recorded. Please record outcome before continuing.'
        );
      }
    });
  }

  closeOB() {
    this.pendingStopRecording = undefined;

    // apparently this should only be clicked if something has gone wrong and they can't get out any other way
    this.submitting.next(true);
    // first check to see if they have logged an outcome.
    this.workSvc.outcome$.pipe(take(1)).subscribe((outcome: any) => {
      if (outcome !== undefined) {
        this.closeOBConfirmed();
      } else {
        this.submitting.next(false);
        this.workSvc.showVerifyCloseModal();
      }
    });
  }

  closeOBConfirmed() {
    this.hangUp().subscribe(() => {
      // submit the record
      this.workSvc
        .submitRecord()
        .pipe(
          withLatestFrom(this.workSvc.master$),
          tap(([submitted, master]) =>
            this.callbackSvc.updateCallback(master?.recordId)
          )
        )
        .subscribe(
          (res) => {
            this.workSvc.closeRecord();
            this.workSvc.unlaunchOB();
            this.workSvc.loadNNData();

            this.submitting.next(false);
            // so we need to check if they also have an IB campaign scheduled
            // if they do they are "blended" so they have IB and OB Preview at the same time
            if (
              this.hasIBScheduled &&
              (this.dialType === 'Preview' || this.dialType === 'Manual')
            ) {
              // in this case we want to move to standby
              // preview
              this.availableib();
            } else {
              // this should also drop the nailup and log the unavailable state
              this.nailOutAndLogout();
              this.workSvc.showStateModal();
            }
          },
          (err) => this.submitting.next(false)
        );
    });
  }

  fastVoicemail() {
    // this.submitRecord();
    return this.workSvc.submitFastVoicemailRecord().pipe(
      withLatestFrom(
        this.nailuptime$,
        this.workSvc.nailupTimeLimit$,
        this.workSvc.campaignId$
      ),
      switchMap(([, nailupstart, nailuplimit, campaignId]) =>
        this.workSvc
          .isUserScheduled(campaignId!)
          .pipe(map((isSched) => [isSched, nailupstart, nailuplimit] as const))
      ),
      map(([isSched, nailupstart, nailuplimit]) => {
        if (!isSched) {
          this.logger.info('DiallerService', 'fastVoicemail() not isSched')();
          this.toastr.info('Shift Ended');
          this.workSvc.exit();
          this.workSvc.navigateToWork();
          this.workSvc.unsetWork();
          this.coreSvc.state = TeleworkerState.Unavailable;
          this.workSvc.showStateModal();
          return false;
        }

        let elapsedtimeminutes = (Date.now() - nailupstart) / 60000;
        if (elapsedtimeminutes >= nailuplimit! && !this.voip) {
          // we need to hangup the nailup call.
          if (this.nailledin) this.nailOutAndLogout();

          this.coreSvc.state = TeleworkerState.Work;
          this.workSvc.openNailupTimeoutModal();
          return false;
        }

        this.workSvc.resetWaitForCall();
        this.getNextNumber();
        this.coreSvc.state = TeleworkerState.AvailableOB;
        return true;
      })
    );
  }

  getNextNumber(): void {
    this.socketOpen = true;
    let req = new GetNextNumberRequest({
      hostname: this.hostname,
      port: this.port,
    });
    this.signalrSend(req);
  }

  getTransferAgents(searchtext: string) {
    var params = {
      search: searchtext,
      hostname: this.hostname,
      port: this.port,
    };
    this.transferAgents$ = this.http
      .get<GetTransferAgentsVm>(`${this.usersApi}/agenttransferlist`, {
        params,
      })
      .pipe(map((vm) => vm.agents));
  }

  hangUp(): Observable<void> {
    if (this.xferStopRecording) this.stopRecording();

    this.xferStopRecording = false;
    this.agent3WayPending = false;

    return defer(() => {
      const req = new HangUpRequest({
        hostname: this.hostname,
        port: +this.port,
      });
      if (this.socketOpen) {
        this.signalrSend(req);

        return merge(
          timer(2000).pipe(
            tap(() => {
              this.logger.warn(
                'DiallerService',
                'hangUp',
                'Timer triggered disconnect'
              );

              this.socketOpen = false;
              this.coreSvc.state = TeleworkerState.CallDisconnected;
            })
          ),
          this.disconnected$.pipe(take(1))
        ).pipe(
          take(1),
          map(() => void 0)
        );
      } else {
        return of(void 0);
      }
    });
  }

  inboundConnect(): void {
    if (this.pendingPopScriptDetails !== undefined) {
      this.workSvc.popScript(
        this.pendingPopScriptDetails.campaignId,
        this.pendingPopScriptDetails.recordId,
        this.pendingPopScriptDetails.DNIS,
        this.pendingPopScriptDetails.queueId, // this has the rapport queueid, queueid has the telephony id (1-99)
        this.pendingPopScriptDetails.recording,
        this.pendingPopScriptDetails.extraData,
        this.pendingPopScriptDetails.waitTime
      );

      this.pendingPopScriptDetails = undefined;
    }

    var hostname: string = this.hostname;
    var port: number = this.port;
    this.signalrSend(new InboundConnectRequest({ hostname, port }));
  }

  inboundTransferOnly(): void {
    // this will update the queues to 100 only for the user.
    // dependant on already being logged in (and nailled up)
    if (this.nailledin) {
      this.signalrSend(new IBTransferOnlyRequest());
      // state change will occer when we receove an IBTransferOnlyEvent event.
    } else {
      this.signalrSend(new LoginIBTransferOnlyRequest());
      this.ibxferonly = true; // set this so we know what to do after we have nailled in.
    }
  }

  isIB() {
    return this.ib;
  }

  launchCampaignIB(campaignId: number, dialType: CampaignDialType) {
    this.coreSvc.campaignId = campaignId;

    this.coreSvc.state$.pipe(take(1)).subscribe((currentstate) => {
      if (currentstate === TeleworkerState.AvailableIB) {
        // we need to cancel getnextnumber()
        this.cancelGetNextNumber();
      } else if (
        currentstate !== TeleworkerState.InboundConnected &&
        currentstate !== TeleworkerState.OutboundConnected &&
        currentstate !== TeleworkerState.WrapOB &&
        currentstate !== TeleworkerState.WrapIB &&
        currentstate !== TeleworkerState.HoldOB &&
        currentstate !== TeleworkerState.HoldIB
      ) {
        this.coreSvc.state = TeleworkerState.UnavailableIB;
      }

      this.workSvc.launchCampaignIB(campaignId, dialType);
    });
  }

  nailInAgain() {
    setTimeout(() => this.reconnectNailin(), 500);
  }

  nailOutAndLogout(): void {
    this.socketOpen = false;
    var hostname: string = this.hostname;
    var port: number = this.port;
    if (hostname != '') {
      this.nailledin = false;
      this.signalrSend(new NailOutAndLogoutRequest({ hostname, port }));
    }

    this.hostname = '';
    this.port = 0;
  }

  nailedup() {
    clearTimeout(this.nailInTimeout);
    // check the registrationFailedState - if Unavailable then we stay in Unavailable state
    this.workSvc.standbyLimit$.pipe(take(1)).subscribe((standbyLimit) => {
      this.logger.info('DiallerService', 'nailedup', {
        ib: this.ib,
        nailInCallLostState: this.nailInCallLostState,
      })();

      if (!!this.callManualPending) {
        let { phoneNumber, callerId, timeout } = this.callManualPending;
        this.callManualPending = undefined;
        this.coreSvc.state = TeleworkerState.CallDialling;
        setTimeout(() => this.callManual(phoneNumber, callerId, timeout), 8000); //need to wait for the tserver to be ready
      } else if (this.nailInCallLostState === undefined) {
        // we aren't recovering from some unexpected nail in call loss so we can go to standby
        if (this.ib === true) {
          if (this.ibxferonly === true) {
            this.coreSvc.state = TeleworkerState.IBTransferOnly;
            this.getNextNumber();
          } else {
            this.coreSvc.state = TeleworkerState.Standby;
            setTimeout(() => this.processNailedUp(), standbyLimit * 1000);
          }
        } else {
          setTimeout(() => this.processNailedUp(), 2000);
        }
      }

      this.nailInCallLostState = undefined;
      this.nailledin = true;
      if (!this.voip) {
        this.nailuptime.next(Date.now());
        setTimeout(() => this.checkNailupTimeout(), 120000);
      }
    });
  }

  next() {
    this.pendingStopRecording = undefined;

    this.submitting.next(true);
    this.workSvc.outcome$.pipe(take(1)).subscribe((outcome) => {
      if (outcome !== undefined) {
        //log the record
        this.workSvc
          .submitRecord()
          .pipe(
            withLatestFrom(this.workSvc.master$),
            tap(([submitted, master]) =>
              this.callbackSvc.updateCallback(master?.recordId)
            )
          )
          .subscribe(
            (res) => {
              this.workSvc.closeRecord();
              this.workSvc.loadNNData();

              this.hangUp()
                .pipe(
                  withLatestFrom(
                    this.workSvc.campaignDialType$,
                    this.workSvc.campaignId$
                  )
                )
                .subscribe(
                  ([, dialType, campaignId]) => {
                    this.finaliseRecord();
                    this.submitting.next(false);
                  },
                  (err) => this.submitting.next(false)
                );
            },
            (err) => this.submitting.next(false)
          );
      } else {
        this.submitting.next(false);
        this.toastr.error(
          'No outcome recorded. Please record outcome before continuing.'
        );
      }
    });
  }

  offHold(): void {
    var hostname: string = this.hostname;
    var port: number = this.port;
    this.signalrSend(new ToggleHoldRequest({ hostname, port }));
    if (this.ib && !this.manualOB)
      this.coreSvc.state = TeleworkerState.InboundConnected;
    else this.coreSvc.state = TeleworkerState.OutboundConnected;
  }

  onHold(): void {
    var hostname: string = this.hostname;
    var port: number = this.port;
    this.signalrSend(new ToggleHoldRequest({ hostname, port }));
    if (this.ib && !this.manualOB) this.coreSvc.state = TeleworkerState.HoldIB;
    else this.coreSvc.state = TeleworkerState.HoldOB;
  }

  postCallSurvey(
    pcstypecustomstring: string,
    phone1: string,
    recordId: number,
    campaignId: number
  ) {
    // [extension data]:tenantid:campaignid:recordid:sessionid:surveytype|custom
    //if (this.manualOB) {
    //  this.hangUp().subscribe();
    //} else {
    this.workSvc.defaultTenantId$.subscribe((tid) => {
      let exten =
        'PCS:' +
        tid?.toString() +
        ':' +
        campaignId?.toString() +
        ':' +
        recordId?.toString() +
        '::' + // empty session string
        pcstypecustomstring;

      this.unattendedTransfer(exten, phone1!, 10);
    });
    //}
  }

  startRecording() {
    this.authSvc.userId$.pipe(take(1)).subscribe((userid) => {
      var filename: string =
        'snoopman_9999_' +
        userid?.toString() +
        '_' +
        formatDate(Date.now(), 'yyyyMMddHHmmss', 'en');
      var hostname: string = this.hostname;
      var port: number = this.port;
      this.signalrSend(
        new AgentRecordingRequest({ hostname, port, filename: filename })
      );

      this.workSvc.addRecording(filename);
    });
  }

  stopRecording() {
    this.authSvc.userId$
      .pipe(withLatestFrom(this.coreSvc.state$), take(1))
      .subscribe(([userid, currstate]) => {
        var req = new AgentRecordingRequest({
          hostname: this.hostname,
          port: this.port,
          filename: `snoopman_${userid?.toString()}_00000000000_${formatDate(
            Date.now(),
            'yyyyMMddHHmmss',
            'en'
          )}_KILLME`,
        });
        if (
          !this.onCallStates.includes(currstate!) ||
          currstate === TeleworkerState.CallDialling
        ) {
          this.logger.info('DiallerService', 'pending stop recording', {
            currstate,
          })();
          this.pendingStopRecording = req;
          return;
        }
        this.signalrSend(req);
      });
  }

  transferToAgent(
    agentId: string,
    leadId: string,
    failoverQueue: string
  ): void {
    var hostname: string = this.hostname;
    var port: number = this.port;
    this.agent3WayPending = false;

    this.coreSvc.campaignId$
      .pipe(
        take(1),
        withLatestFrom(this.workSvc.master$, this.workSvc.scheduledCampaigns$)
      )
      .subscribe(([campaignId, master, schedcampaigns]) => {
        if (!campaignId || campaignId < 1) {
          // if we have only 1 IB then use that one.
          const ibCampaigns = schedcampaigns?.filter((camp) =>
            camp.actionType.startsWith('IB')
          );
          if (ibCampaigns?.length === 1) {
            campaignId = ibCampaigns[0].campaignId;
            this.workSvc.selectCampaign(campaignId);
          }
        }

        if (leadId === '' || leadId === '0') {
          if (master !== undefined && master.recordId !== undefined) {
            leadId = master.recordId.toString();
          } else {
            leadId = campaignId!.toString();
          }
        }

        this.signalrSend(
          new TransferToAgentRequest({
            agentId,
            leadId,
            failoverQueue,
            hostname,
            port,
          })
        );

        // move into relevant Transfer presence
        // clicking hangup (to leave caller and new agent connected)
        // will initiate move into Wrap state.
        if (this.ib && !this.onVoicemailCall)
          this.coreSvc.state = TeleworkerState.TransferIB;
        else this.coreSvc.state = TeleworkerState.TransferOB;
      });
  }
}

const isNotificationSupported = () =>
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window;
